// 🔥 인라인 편집 가능한 가격 셀 컴포넌트 (일괄 저장 방식)
// [배경] 엑셀처럼 셀 클릭 시 즉시 인라인 편집 모드로 전환
//        Enter/Tab: 다음 셀 이동 (저장은 "저장 버튼"으로 일괄)
//        변경된 셀은 노란색으로 강조, 저장 완료 시 초록색 페이드아웃
//        React.memo로 최적화하여 필요한 셀만 리렌더링

import React, { useState, useRef, useEffect, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { Box, TextField, Typography, InputAdornment } from '@mui/material';
import { keyframes } from '@mui/system';

// 🎨 저장 완료 시 강조 애니메이션 (초록색 → 흰색 페이드아웃)
const savedFlash = keyframes`
  0% {
    background-color: #10b981;
    color: white;
  }
  70% {
    background-color: #10b981;
    color: white;
  }
  100% {
    background-color: transparent;
    color: inherit;
  }
`;

// 🔑 셀 식별을 위한 인터페이스
export interface CellPosition {
  priceId: string;
  grade: 'A' | 'B' | 'C' | 'D' | 'E';
  rowIndex: number;
  colIndex: number; // 0=A, 1=B, 2=C, 3=D, 4=E
}

// 📌 Props 인터페이스
export interface EditablePriceCellProps {
  /** 현재 가격 값 (원본) */
  value: number;
  /** 표시할 값 (변경된 값 포함) */
  displayValue: number;
  /** 셀 위치 정보 */
  position: CellPosition;
  /** 값 변경 콜백 (로컬 상태 저장, Firebase X) */
  onChange: (position: CellPosition, newValue: number) => void;
  /** 다음/이전 셀로 이동 콜백 */
  onNavigate: (from: CellPosition, direction: 'next' | 'prev' | 'up' | 'down') => void;
  /** 편집 모드 시작 알림 */
  onEditStart?: (position: CellPosition) => void;
  /** 편집 모드 종료 알림 */
  onEditEnd?: (position: CellPosition) => void;
  /** 등급별 색상 */
  gradeColor?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 외부에서 편집 모드 제어 */
  forceEdit?: boolean;
  /** 이 셀이 변경되었는지 (노란색 강조용) */
  isChanged?: boolean;
  /** 이 셀이 저장 완료되었는지 (초록색 애니메이션용) */
  isSaved?: boolean;
}

// 📌 Ref 인터페이스 (외부에서 호출 가능한 메서드)
export interface EditablePriceCellRef {
  /** 편집 모드 시작 */
  startEdit: () => void;
  /** 편집 모드 종료 */
  cancelEdit: () => void;
  /** 현재 편집 중인지 */
  isEditing: () => boolean;
  /** 포커스 설정 */
  focus: () => void;
}

// 🔥 인라인 편집 가격 셀 컴포넌트 (일괄 저장 방식)
const EditablePriceCell = forwardRef<EditablePriceCellRef, EditablePriceCellProps>(({
  value,
  displayValue,
  position,
  onChange,
  onNavigate,
  onEditStart,
  onEditEnd,
  gradeColor = '#374151',
  disabled = false,
  forceEdit = false,
  isChanged = false,
  isSaved = false
}, ref) => {
  // 📊 상태 관리
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(displayValue.toString());
  
  // 📌 Ref
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  
  // 🔄 외부 값 변경 시 동기화
  useEffect(() => {
    if (!isEditing) {
      setEditValue(displayValue.toString());
    }
  }, [displayValue, isEditing]);
  
  // 🔄 forceEdit prop으로 편집 모드 제어
  useEffect(() => {
    if (forceEdit && !isEditing && !disabled) {
      startEditMode();
    }
  }, [forceEdit]);
  
  // ✏️ 편집 모드 시작
  const startEditMode = useCallback(() => {
    if (disabled) return;
    
    setIsEditing(true);
    setEditValue(displayValue.toString());
    onEditStart?.(position);
    
    // 다음 렌더 후 input에 포커스 및 전체 선택
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  }, [disabled, displayValue, onEditStart, position]);
  
  // ❌ 편집 취소 (원본 값으로 복원)
  const cancelEditMode = useCallback(() => {
    setIsEditing(false);
    setEditValue(displayValue.toString());
    onEditEnd?.(position);
  }, [displayValue, onEditEnd, position]);
  
  // 💾 값 확정 (로컬 상태에 저장, Firebase X)
  const confirmValue = useCallback(() => {
    const newValue = parseInt(editValue.replace(/[^0-9]/g, '')) || 0;
    
    // 변경사항을 로컬 상태에 저장
    onChange(position, newValue);
    
    setIsEditing(false);
    onEditEnd?.(position);
  }, [editValue, onChange, position, onEditEnd]);
  
  // ⌨️ 키보드 이벤트 처리
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        confirmValue();
        break;
        
      case 'Tab':
        e.preventDefault();
        confirmValue();
        // Tab: 다음 셀, Shift+Tab: 이전 셀
        onNavigate(position, e.shiftKey ? 'prev' : 'next');
        break;
        
      case 'Escape':
        e.preventDefault();
        cancelEditMode();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        confirmValue();
        onNavigate(position, 'up');
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        confirmValue();
        onNavigate(position, 'down');
        break;
    }
  }, [confirmValue, cancelEditMode, onNavigate, position]);
  
  // 🎯 입력값 변경 (숫자만 허용)
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    setEditValue(rawValue);
  }, []);
  
  // 🖱️ 셀 클릭 핸들러
  const handleCellClick = useCallback(() => {
    if (!isEditing && !disabled) {
      startEditMode();
    }
  }, [isEditing, disabled, startEditMode]);
  
  // 📌 외부에서 사용 가능한 메서드 노출
  useImperativeHandle(ref, () => ({
    startEdit: startEditMode,
    cancelEdit: cancelEditMode,
    isEditing: () => isEditing,
    focus: () => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      } else {
        startEditMode();
      }
    }
  }), [startEditMode, cancelEditMode, isEditing]);
  
  // 🎨 포맷팅: 숫자를 천단위 콤마로 표시
  const formatPrice = (val: number): string => {
    if (val === 0) return '미설정';
    return `${val.toLocaleString()}원`;
  };
  
  // 🎨 렌더링
  return (
    <Box
      ref={cellRef}
      onClick={handleCellClick}
      sx={{
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        px: 0.5,
        py: 0.25,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        position: 'relative',
        // 🎨 저장 완료 애니메이션 (초록색 → 흰색 페이드아웃)
        ...(isSaved && {
          animation: `${savedFlash} 1.5s ease-out`
        }),
        // 🎨 변경됨 표시 (노란색/주황색 배경)
        ...(isChanged && !isSaved && !isEditing && {
          bgcolor: 'rgba(251, 191, 36, 0.25)',
          border: '2px solid #f59e0b',
          borderRadius: 1,
          '&::after': {
            content: '"●"',
            position: 'absolute',
            top: -2,
            right: -2,
            fontSize: '8px',
            color: '#f59e0b'
          }
        }),
        // 🎨 편집 모드 스타일
        ...(isEditing && {
          bgcolor: 'rgba(59, 130, 246, 0.15)',
          border: '2px solid #3b82f6',
          borderRadius: 1,
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
        }),
        // 🎨 호버 스타일
        ...(!isEditing && !disabled && !isChanged && {
          '&:hover': {
            bgcolor: 'rgba(59, 130, 246, 0.08)',
            outline: '1px dashed #94a3b8'
          }
        })
      }}
    >
      {isEditing ? (
        // ✏️ 편집 모드
        <TextField
          inputRef={inputRef}
          value={editValue ? parseInt(editValue).toLocaleString() : ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // blur 시 값 확정 (다른 곳 클릭 시)
            confirmValue();
          }}
          size="small"
          variant="standard"
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">원</Typography>
              </InputAdornment>
            ),
            sx: {
              fontSize: '0.85rem',
              fontWeight: 600,
              fontFamily: 'monospace',
              textAlign: 'right',
              color: gradeColor,
              '& input': {
                textAlign: 'right',
                padding: '2px 4px'
              }
            }
          }}
          sx={{
            width: '100%',
            '& .MuiInput-root': {
              fontSize: '0.85rem'
            }
          }}
        />
      ) : (
        // 📊 표시 모드
        <Typography
          variant="body2"
          sx={{
            fontWeight: isChanged ? 700 : 600,
            color: displayValue === 0 ? '#94a3b8' : (isChanged ? '#b45309' : gradeColor),
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            userSelect: 'none'
          }}
        >
          {formatPrice(displayValue)}
        </Typography>
      )}
    </Box>
  );
});

// 🔧 displayName 설정
EditablePriceCell.displayName = 'EditablePriceCell';

// 🔥 React.memo로 최적화된 컴포넌트 내보내기
// props가 변경되지 않으면 리렌더링 방지
export default memo(EditablePriceCell, (prevProps, nextProps) => {
  // 핵심 props가 같으면 리렌더링 스킵
  return (
    prevProps.value === nextProps.value &&
    prevProps.displayValue === nextProps.displayValue &&
    prevProps.position.priceId === nextProps.position.priceId &&
    prevProps.position.grade === nextProps.position.grade &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.forceEdit === nextProps.forceEdit &&
    prevProps.gradeColor === nextProps.gradeColor &&
    prevProps.isChanged === nextProps.isChanged &&
    prevProps.isSaved === nextProps.isSaved
  );
});
